import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { InformationCircle } from 'assets/icons';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import JustificatifConfirmation, {
  CONFIRMATION_TITLE,
} from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import useBaseline from 'hooks/useBaseline';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import { increaseLabel } from 'helper/remunerationDraftChange';

const DRAFT_KEY = 'holidayHours';

// Empty until the company says otherwise: pre-selecting the Whit Monday made an
// unanswered question look like a decision. The special day (JBWEB-1467) is
// the one holiday with its own rate (« le 01/05 à 100 % »), none by default.
const EMPTY_FORM = { percentage: '', solidarityDay: '', specialDay: '', specialPercentage: '' };

const asText = (value) => (value === undefined || value === null ? '' : String(value));
const parsed = (value) => parseFloat(String(value).replace(',', '.'));

// The form as the api stores it: `holidayHours` with its special day.
const formOf = (holidayHours) => ({
  percentage: asText(holidayHours?.percentage),
  solidarityDay: holidayHours?.solidarityDay ?? '',
  specialDay: holidayHours?.specialDay?.slug ?? '',
  specialPercentage: asText(holidayHours?.specialDay?.percentage),
});

const validate = (value) => {
  const number = parsed(value);
  if (!Number.isFinite(number)) return 'Saisissez un nombre.';
  if (number < 0 || number > 100) return 'La majoration doit être comprise entre 0 et 100.';
  if (Math.round(number * 10) / 10 !== number) return 'La majoration accepte au plus une décimale.';
  return null;
};

const ModalHolidayHoursCompany = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [form, setForm] = useState(EMPTY_FORM);
  const [holidays, setHolidays] = useState([]);
  const [holidaysError, setHolidaysError] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const justificatif = useJustificatifConfirmation();
  const baseline = useBaseline(EMPTY_FORM);
  const token = localStorage.getItem('xsrfToken');
  // The page's changes held back (JBWEB-1468): this one joins them instead of
  // asking for its own justificatif.
  const draft = modalData?.draft;
  const staged = draft?.pending(DRAFT_KEY);

  const change = (field) => (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      [field]: value,
      // No day, no rate of its own.
      ...(field === 'specialDay' && !value && { specialPercentage: '' }),
    }));
    setErrors((previous) => ({ ...previous, [field]: null, ...(field === 'specialDay' && { specialPercentage: null }) }));
  };

  useEffect(() => {
    if (!modalData?.idCompany) return;
    ApiDatabase.getHolidayHoursCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        // The modal opens on the current values: it serves for the initial
        // entry as well as for edits (RG-1).
        const loaded = formOf(data?.holidayHours);
        setForm(staged ?? loaded);
        baseline.capture(loaded);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );

    ApiDatabase.getHolidays(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setHolidays(data.holidays ?? []);
        setHolidaysError(null);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        // An empty dropdown with no explanation reads as "there is nothing to
        // choose", which is not what happened.
        setHolidaysError("La liste des jours fériés n'a pas pu être chargée. Réessayez plus tard.");
      }
    );
  }, [modalData?.idCompany]);

  const labelOf = (slug) => holidays.find((holiday) => holiday.slug === slug)?.label ?? null;

  const validateForm = () => {
    const found = {
      percentage: validate(form.percentage),
      specialPercentage: form.specialDay ? validate(form.specialPercentage) : null,
      // A day is either worked without increase or increased on its own rate.
      specialDay:
        form.specialDay && form.specialDay === form.solidarityDay
          ? 'Ce jour est la journée de solidarité : choisissez-en un autre.'
          : null,
    };
    setErrors(found);
    return !Object.values(found).some(Boolean);
  };

  // As the api takes it: the solidarity day and the special day are stored as
  // absent rather than empty, so they read like a company that never had one.
  const holidayHoursOf = () => ({
    percentage: parsed(form.percentage),
    solidarityDay: form.solidarityDay || null,
    specialDay: form.specialDay ? { slug: form.specialDay, percentage: parsed(form.specialPercentage) } : null,
  });

  // The panel patches itself rather than refetching, and only this modal holds
  // the holiday list that names the slugs — so the labels travel with the
  // change instead of the card fetching them back.
  const labels = () => ({
    solidarityDayLabel: labelOf(form.solidarityDay),
    specialDayLabel: labelOf(form.specialDay),
  });

  const described = (values) =>
    [
      increaseLabel(values.percentage),
      values.specialDay && `${labelOf(values.specialDay)} ${increaseLabel(values.specialPercentage)}`,
      `solidarité : ${values.solidarityDay ? labelOf(values.solidarityDay) : 'aucune'}`,
    ]
      .filter(Boolean)
      .join(' · ');

  const handleSubmit = () => {
    if (!validateForm()) return;

    // These values reach contracts and invoices, so the change carries the
    // client's written request for it before anything is written.
    // Nothing to confirm when nothing moved: the confirmation asks for a
    // justificatif, and there would be no change for it to justify.
    if (draft) {
      if (!baseline.hasChanged(form)) draft.remove(DRAFT_KEY);
      else
        draft.stage({
          key: DRAFT_KEY,
          section: DRAFT_KEY,
          label: 'Jours fériés travaillés',
          before: described(baseline.value()),
          after: described(form),
          form,
          preview: { type: 'holiday_hours_updated', holidayHours: holidayHoursOf(), ...labels() },
          commit: send,
        });
      return toggle();
    }
    if (!baseline.hasChanged(form)) return;
    if (!justificatif.isConfirming) return justificatif.setIsConfirming(true);
    justificatif.confirm(save);
  };

  // The save, as the modal return it resolves with: run here, or later with
  // the page's other changes.
  const send = (storedJustificatif) =>
    new Promise((resolve, reject) =>
      ApiDatabase.postHolidayHoursCompany(
        {
          token,
          justificatif: storedJustificatif,
          idCompany: modalData.idCompany,
          ...holidayHoursOf(),
        },
        (data) => resolve({ type: 'holiday_hours_updated', holidayHours: data.holidayHours, ...labels() }),
        reject,
      ),
    );

  const save = (storedJustificatif) => {
    setIsLoading(true);
    send(storedJustificatif).then(
      (saved) => {
        setIsLoading(false);
        toast.success('La majoration des heures de jours fériés a été enregistrée');
        setModalDataReturn(saved);
        toggle();
      },
      (err) => {
        setIsLoading(false);
        if (err.name === 'CanceledError') return;
        // The modal stays open, the input intact (RG-6).
        setErrors({ percentage: err?.response?.data?.message ?? "L'enregistrement a échoué" });
      },
    );
  };

  // The list comes from calendrier.api.gouv.fr: while it is missing, the
  // stored day is kept as the only option rather than lost.
  const dayOptions = (value, empty) =>
    holidays.length === 0 ? (
      <option value={value}>{holidaysError ? 'Indisponible' : 'Chargement…'}</option>
    ) : (
      <>
        <option value={''}>{empty}</option>
        {holidays.map((holiday) => (
          <option key={holiday.slug} value={holiday.slug}>
            {holiday.label}
          </option>
        ))}
      </>
    );

  const percentageInput = (field, label) => (
    <div className={'flex items-center gap-2'}>
      <input
        type={'text'}
        aria-label={label}
        className={`border rounded px-2 py-2 w-full ${errors[field] ? 'border-red-500' : ''}`}
        value={form[field]}
        onChange={change(field)}
      />
      <span className={'text-gray-500'}>%</span>
    </div>
  );
  const errorOf = (field) => errors[field] && <div className={'text-red-500 text-xs'}>{errors[field]}</div>;

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Heures jours fériés travaillés'}
      </Modalheader>
      <ModalBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
          />
        ) : (
          <div className={'flex flex-col gap-3 text-sm'}>
            <div className={'text-gray-500'}>
              Cette majoration s’applique à tous les jours fériés travaillés, sauf la journée de
              solidarité et le jour qui a sa propre majoration.
            </div>
            <label className={'flex flex-col gap-1'}>
              Majoration
              {percentageInput('percentage', 'Majoration')}
            </label>
            {errorOf('percentage')}
            <div className={'flex flex-col gap-1'}>
              <span>
                Jour avec une majoration particulière <span className={'text-gray-500'}>(facultatif)</span>
              </span>
              <div className={'grid grid-cols-[minmax(0,1fr)_120px] gap-2'}>
                <select
                  aria-label={'Jour avec une majoration particulière'}
                  className={`border rounded px-2 py-2 ${errors.specialDay ? 'border-red-500' : ''}`}
                  value={form.specialDay}
                  disabled={holidays.length === 0}
                  onChange={change('specialDay')}
                >
                  {dayOptions(form.specialDay, 'Aucun')}
                </select>
                {form.specialDay ? (
                  percentageInput('specialPercentage', 'Majoration du jour particulier')
                ) : (
                  <span />
                )}
              </div>
              {errorOf('specialDay')}
              {errorOf('specialPercentage')}
            </div>
            <label className={'flex flex-col gap-1'}>
              <span className={'flex items-center justify-between'}>
                <span>
                  Journée de solidarité <span className={'text-gray-500'}>(facultatif)</span>
                </span>
                {/* The modal shell sits at z-50, the same layer the tooltip
                    defaults to: without a higher one the bubble can land behind
                    the modal depending on portal mount order. */}
                <Tooltip
                  text={
                    'La journée de solidarité est travaillée sans majoration : elle finance l’autonomie des personnes âgées et handicapées. Le jour choisi ici est exclu du calcul ; sans journée choisie, tous les jours fériés sont majorés.'
                  }
                  placement={'top'}
                  zIndex={60}
                >
                  <span className={'cursor-help'}>
                    <InformationCircle wh={20} color={'#9CA3AF'} />
                  </span>
                </Tooltip>
              </span>
              <select
                className={'border rounded px-2 py-2'}
                value={form.solidarityDay}
                disabled={holidays.length === 0}
                onChange={change('solidarityDay')}
              >
                {dayOptions(form.solidarityDay, 'Aucune')}
              </select>
              {holidaysError && <div className={'text-red-500 text-xs'}>{holidaysError}</div>}
            </label>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          size={'LG'}
          color={'SECONDARY'}
          // Backs out of the confirmation, not the modal: the values being
          // confirmed are still worth seeing.
          onClick={justificatif.isConfirming ? () => justificatif.setIsConfirming(false) : toggle}
        >
          Annuler
        </Button>
        <Button
          size={'LG'}
          color={
            isLoading ||
            justificatif.isUploading ||
            (!baseline.hasChanged(form) && staged === undefined) ||
            (justificatif.isConfirming && !justificatif.canConfirm)
              ? 'DISABLED'
              : 'PRIMARY'
          }
          onClick={isLoading || justificatif.isUploading ? undefined : handleSubmit}
        >
          {isLoading || justificatif.isUploading ? 'Confirmation ...' : draft ? 'Ajouter aux modifications' : 'Confirmer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalHolidayHoursCompany;
